// talent-qr-code-validation.service.ts - FIXED VERSION with iOS support
import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { PopupService } from 'src/modules/shared/services/popup.service';
import { QrScannerModalComponent } from 'src/modules/shared/components/qr-scanner-modal/qr-scanner-modal.component';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { ITalentJob } from '../interfaces/talent-job.interface';
import { TalentDashboardService } from './talent-dashboard.service';
import { QRValidationEnum } from 'src/modules/shared/enums/qr-scanner-validation.enum';
import { LoadingService } from 'src/modules/shared/services/loading.service';
import { CameraPermissionService } from 'src/modules/shared/services/camera-permission.service';
import { Subscription } from 'rxjs';

type QRActionType = 'CLOCK_IN' | 'CLOCK_OUT';

@Injectable({ providedIn: 'root' })
export class TalentQrCodeValidationService implements OnDestroy {
  private activePopup: any = null;
  private isValidating = false;
  private scannerOpened = false;
  private permissionSubscription: Subscription | null = null;
  private permissionStatusSubscription: Subscription | null = null;
  private currentLoadingRef: any = null;
  private cleanupInProgress = false;
  private validationPromise: Promise<boolean> | null = null;
  private permissionRequestInProgress = false;
  private waitingForPermissionFromSettings = false;
  private pendingValidationData: {
    userData: IUser;
    job: ITalentJob;
    actionType: QRActionType;
  } | null = null;

  private isIOS: boolean = false;
  private isAndroid: boolean = false;

  constructor(
    private modalController: ModalController,
    private popupService: PopupService,
    private talentDashboardService: TalentDashboardService,
    private platform: Platform,
    private loadingService: LoadingService,
    private cameraPermission: CameraPermissionService,
    private ngZone: NgZone
  ) {
    this.isIOS = this.platform.is('ios');
    this.isAndroid = this.platform.is('android');
    console.log('[QR Validation] Service initialized - iOS:', this.isIOS, this.isAndroid);
    this.setupPermissionMonitoring();
  }

  private setupPermissionMonitoring(): void {
  // Monitor permission revocation
  this.permissionSubscription = this.cameraPermission.permissionChanges.subscribe(
    async (granted) => {
      if (this.scannerOpened && !granted) {
        await this.handlePermissionRevoked();
      }
    }
  );
  
  // REMOVE the permissionStatusSubscription completely
  // We don't want auto-resume, user must click "Scan QR" again
}

  private async handlePermissionGrantedFromSettings(): Promise<void> {
    if (this.cleanupInProgress) {
      console.log('[QR Validation] Cleanup in progress — skipping permission recovery');
      return;
    }

    if (this.permissionRequestInProgress) {
      console.log('[QR Validation] Permission request still in progress — skipping settings recovery');
      return;
    }

    if (!this.waitingForPermissionFromSettings) {
      console.log('[QR Validation] Not waiting for settings permission — ignoring');
      return;
    }

    // Guard: Prevent double execution
    this.waitingForPermissionFromSettings = false;

    console.log('[QR Validation] Handling post-settings permission recovery...');

    // Hide any popups/loaders safely
    await this.dismissActivePopup();
    await this.hideCurrentLoading();

    // If no pending validation data, just stop
    if (!this.pendingValidationData) {
      console.log('[QR Validation] No pending validation data found — nothing to resume');
      return;
    }

    const { userData, job, actionType  } = this.pendingValidationData;

    // Reset state to allow validation retry
    this.isValidating = false;
    this.validationPromise = null;

    try {
      console.log('[QR Validation] Resuming QR validation after settings...');
      const result = await this.validateQrForShift(userData, job, true, actionType );

      if (result) {
        console.log('[QR Validation] Validation successful after permission recovery');
      } else {
        console.log('[QR Validation] Validation failed after permission recovery');
      }
    } catch (error) {
      console.error('[QR Validation] Error resuming validation after settings:', error);
      await this.showErrorPopup('An error occurred while resuming QR validation. Please try again.');
    } finally {
      // Cleanup pending state
      this.pendingValidationData = null;
    }
  }

  private async handlePermissionRevoked(): Promise<void> {
    await this.dismissActivePopup();
    await this.hideCurrentLoading();

    const topModal = await this.modalController.getTop();
    if (topModal?.component === QrScannerModalComponent) {
      try {
        await topModal.dismiss({
          error: 'Camera permission was revoked',
          needsPermission: true,
        });
        this.scannerOpened = false;
      } catch (err) {
        console.error('[QrValidation] Error dismissing scanner after revocation:', err);
      }
    }
  }

  async validateQrForShift(
  userData: IUser,
  job: ITalentJob,
  isRetry = false,
  actionType: QRActionType = 'CLOCK_IN'
): Promise<boolean> {
  // ✅ ADD THIS CHECK FIRST - Bypass everything if QR not required
  try {
    this.currentLoadingRef = await this.loadingService.showLoading().toPromise();
    
    const isQRCodeEnabledForClockIn = await this.talentDashboardService.ensureQRCodeCheck(job.entity._id);
    console.log('[QR Validation] QR code required for job:', isQRCodeEnabledForClockIn);
    
    await this.hideCurrentLoading();
    
    if (!isQRCodeEnabledForClockIn.codeRequired || job.isVisitBasedShift) {
      console.log('[QR Validation] QR code not required - bypassing validation');
      return true; // Return true to allow shift to continue
    }
  } catch (error) {
    console.error('[QR Validation] Error checking QR requirement:', error);
    await this.hideCurrentLoading();
    // If the check fails we cannot determine the requirement — default to
    // allowing the action rather than blocking users on QR-disabled clients.
    return true;
  }
  
  // CRITICAL: Prevent multiple simultaneous validations
  if (this.isValidating && this.validationPromise) {
    console.log('[QR Validation] Already validating, returning existing promise');
    return this.validationPromise;
  }

  // Create new validation promise
  this.validationPromise = this.executeValidation(userData, job, isRetry, actionType);
  
  try {
    const result = await this.validationPromise;
    return result;
  } finally {
    this.validationPromise = null;
  }
}

  private async executeValidation(
  userData: IUser,
  job: ITalentJob,
  isRetry: boolean,
  actionType: QRActionType = 'CLOCK_IN'
): Promise<boolean> {
  this.isValidating = true;
  let cleanedUp = false;

  const cleanup = async () => {
    if (cleanedUp) return;
    cleanedUp = true;
    
    await this.hideCurrentLoading();
    this.isValidating = false;
  };

  try {
    console.log('[QR Validation] Starting validation for action:', actionType);

    // Step 2: Show QR required popup (only if not retry)
    await this.dismissActivePopup();

    if (!isRetry) {
      console.log('[QR Validation] Showing QR required popup...');
      
      // NEW: Select correct enum based on action type
      const enumKey = actionType === 'CLOCK_OUT' 
        ? 'QR_CODE_REQUIRED_CLOCK_OUT' 
        : 'QR_CODE_REQUIRED';
      
        const userDecision = await new Promise<boolean>((resolve) => {
        this.popupService.showModal(
          QRValidationEnum[enumKey],
          () => {
            console.log('[QR Validation] User accepted popup');
            resolve(true);
          },
          () => {
            console.log('[QR Validation] User rejected popup');
            resolve(false);
          }
        ).then(popup => {
          this.activePopup = popup;
        });
      });

      if (!userDecision) {
        console.log('[QR Validation] User cancelled popup - stopping flow');
        await cleanup();
        return false;
      }
    }

    await this.dismissActivePopup();

    // Handle permissions
    console.log('[QR Validation] Handling permissions...');
    const permissionResult = await this.handlePermissions();
    console.log('[QR Validation] Permission result:', permissionResult);
    
    if (!permissionResult.granted) {
      console.log('[QR Validation] Permission not granted');
      await this.dismissActivePopup();
      
      if (permissionResult.showSettings) {
        console.log('[QR Validation] Showing settings popup');
        
        // Show settings popup with correct type (native or browser denial)
        await this.showPermissionDeniedError(
          permissionResult.missing!,
          permissionResult.isBrowserDenial || false
        );
        
        // User must click "Scan QR" again after granting permission
        await cleanup();
        return false;
      } else {
        // Soft denial - show appropriate message
        if (permissionResult.isBrowserDenial) {
          console.log('[QR Validation] Browser soft denial');
          await this.showPermissionDeniedError('CAMERA', true);
        }
        await cleanup();
      }
      return false;
    }

    // Permission granted - continue with scanner
    console.log('[QR Validation] Permission granted, opening scanner...');
    
    const scanResult = await this.openQrScanner(job);
    console.log('[QR Validation] Scan result:', scanResult);
    
    if (!scanResult.success) {
      await cleanup();
      
      if (scanResult.needsPermission) {
        console.log('[QR Validation] Permission needed, retrying...');
        return await this.executeValidation(userData, job, true, actionType);
      }
      
      if (!scanResult.error) {
        console.log('[QR Validation] Scanner cancelled');
        return false;
      }
      
      console.error('[QR Validation] Scanner error:', scanResult.error);
      await this.showErrorPopup(scanResult.error);
      return false;
    }

    if (!scanResult.scannedText) {
      console.error('[QR Validation] No scanned text');
      await cleanup();
      return false;
    }

    // Validate scanned code
    console.log('[QR Validation] Validating code...');
    const validationSuccess = await this.validateScannedCode(
      job.entity._id,
      scanResult.scannedText,
      userData,
      job,
      actionType
    );
    
    await cleanup();
    return validationSuccess;

  } catch (error: any) {
    console.error('[QR Validation] Validation error:', error);
    await cleanup();
    await this.showErrorPopup(
      error.message || 'An unexpected error occurred. Please try again.'
    );
    return false;
  }
}

  private async handlePermissions(): Promise<{
  granted: boolean;
  showSettings: boolean;
  missing?: 'CAMERA' | 'MEDIA' | 'BOTH';
  needsReload?: boolean;
  isBrowserDenial?: boolean;  // NEW
}> {
  this.permissionRequestInProgress = true;
  console.log('[QR Validation] === Starting Permission Check ===');
  
  await this.dismissActivePopup();
  await this.hideCurrentLoading();

  try {
    let result;
    
    if (this.isIOS) {
      // iOS: Check native first, then WKWebView
      try {
        result = await this.cameraPermission.requestPermissionIfNeeded();
        
        console.log('[QR Validation] iOS permission result:', result);
        
        // CRITICAL: Determine if denial was native or browser
        let isBrowserDenial = false;
        
        if (!result.granted) {
          // Check if native permission is denied
          const nativeDenied = await this.cameraPermission.isNativePermissionDenied();
          
          if (nativeDenied) {
            // Native is denied - show native settings popup
            console.log('[QR Validation] Native permission denied');
            isBrowserDenial = false;
          } else {
            // Native is granted but browser denied - show browser retry popup
            console.log('[QR Validation] Browser permission denied');
            isBrowserDenial = true;
          }
        }
        
        return {
          granted: result.granted,
          showSettings: result.showSettings,
          missing: 'CAMERA',
          isBrowserDenial
        };
      } catch (error: any) {
        if (error.message === 'WKWEBVIEW_PERMISSION_DENIED') {
          console.log('[QR Validation] WKWebView permission issue detected');
          
          // Show message that app needs restart
          await this.showWKWebViewRestartMessage();
          
          return { 
            granted: false, 
            showSettings: false, 
            needsReload: true 
          };
        }
        throw error;
      }
    } else {
      // Android: Use checkAndRequestPermissions
      result = await this.cameraPermission.checkAndRequestPermissions();
      
      console.log('[QR Validation] Android permission result:', result);
      
      const granted = result.camera;
      const showSettings = !granted && this.cameraPermission.shouldShowSettings();
      
      return {
        granted,
        showSettings,
        missing: 'CAMERA',
        isBrowserDenial: this.isAndroid ? false : true  // Android only has native permissions
      };
    }
  } catch (error) {
    console.error('[QR Validation] ❌ Permission error:', error);
    return { granted: false, showSettings: false };
  } finally {
    this.permissionRequestInProgress = false;
  }
}

private async showWKWebViewRestartMessage(): Promise<void> {
  await this.popupService.showModal({
    heading: 'App Restart Required',
    message: 'Camera access was updated. Please restart the app to enable it.\n\n' +
             '1. Close this app completely (swipe up in app switcher)\n' +
             '2. Reopen the app\n' +
             '3. Tap "Scan QR" again and allow camera access when prompted',
    btn: 'Close App',
    navigateRoute: '',
    imgURL: 'assets/images/info.png'
  });
  
  // Optionally force close (not recommended for App Store)
  // if (this.platform.is('cordova')) {
  //   (navigator as any).app.exitApp();
  // }
}

  private async showPermissionDeniedError(
  missing: 'CAMERA' | 'MEDIA' | 'BOTH',
  isBrowserDenial: boolean = false
): Promise<'opened_settings' | 'cancelled'> {
  // CRITICAL: Ensure all UI elements are cleaned up first
  await this.dismissActivePopup();
  await this.hideCurrentLoading();
  
  const topModal = await this.modalController.getTop();
  if (topModal) {
    try {
      await topModal.dismiss();
      // Wait for modal to fully dismiss
      await this.delay(200);
    } catch (err) {
      console.error('[QR Validation] Error dismissing modal:', err);
    }
  }

  // Add delay to ensure UI is completely ready
  await this.delay(300);
  
  console.log('[QR Validation] Showing settings popup for:', missing, 'isBrowserDenial:', isBrowserDenial);
  
  // CRITICAL: Run in NgZone to ensure Angular change detection
  return await this.ngZone.run(async () => {
    const result = await this.cameraPermission.showSettingsPopup(missing, isBrowserDenial);
    console.log('[QR Validation] Settings popup result:', result);
    return result;
  });
}

  private async openQrScanner(
    job: ITalentJob
  ): Promise<{
    success: boolean;
    scannedText?: string;
    error?: string;
    needsPermission?: boolean;
  }> {
    // Prevent opening multiple scanners
    if (this.scannerOpened) {
      console.log('[QR Validation] Scanner already opened');
      return { success: false, error: 'Scanner already open' };
    }

    try {
      await this.dismissActivePopup();
      await this.hideCurrentLoading();

      this.scannerOpened = true;

      this.activePopup = await this.modalController.create({
        component: QrScannerModalComponent,
        cssClass: 'qrScannerModal',
        backdropDismiss: false,
        componentProps: {
          job: job,
          platform: this.platform.platforms(),
        },
      });

      await this.activePopup.present();

      const { data } = await this.activePopup.onWillDismiss();
      this.activePopup = null;
      this.scannerOpened = false;

      if (data?.cancelled) {
        return { success: false };
      }
      if (data?.scannedText) {
        return { success: true, scannedText: data.scannedText };
      }
      if (data?.error) {
        return {
          success: false,
          error: data.error,
          needsPermission: data.needsPermission,
        };
      }

      return { success: false };
    } catch (error: any) {
      console.error('[QR Validation] Modal error:', error);
      this.activePopup = null;
      this.scannerOpened = false;
      return { success: false, error: error.message };
    }
  }

  private async validateScannedCode(
    jobId: string,
    scannedText: string,
    userData: IUser,
    job: ITalentJob,
    actionType: QRActionType = 'CLOCK_IN'
  ): Promise<boolean> {
    await this.hideCurrentLoading();
    
    this.currentLoadingRef = await this.loadingService.showLoading().toPromise();

    try {
      await this.talentDashboardService.validateQRCode(job.entity._id, scannedText, actionType, job._id);

      await this.hideCurrentLoading();
      return true;
    } catch (error: any) {
      await this.hideCurrentLoading();
      return await this.handleScanFailure(userData, job, actionType);
    }
  }

  private async showErrorPopup(message: string): Promise<void> {
    await this.dismissActivePopup();
    await this.hideCurrentLoading();
    
    this.activePopup = await this.popupService.showModal({
      heading: 'Error',
      message,
      btn: 'OK',
      navigateRoute: null,
      imgURL: 'assets/images/error.png'
    });
  }

  private async handleScanFailure(
    userData: IUser,
    job: ITalentJob,
    actionType: QRActionType = 'CLOCK_IN'
  ): Promise<boolean> {
    await this.dismissActivePopup();
    await this.hideCurrentLoading();

    return new Promise<boolean>((resolve) => {
      this.popupService
        .showModal(
          {
            heading: 'Invalid QR Code',
            message:
              'The scanned QR code is not valid for this job. Please try scanning again.',
            btn: 'Retry',
            rejectBtnText: 'Cancel',
            navigateRoute: '',
            imgURL: 'assets/images/error.png'
          },
          async () => {
            // CRITICAL: Reset state before retry
            this.isValidating = false;
            this.pendingValidationData = null;
            this.waitingForPermissionFromSettings = false;
            
            const retryResult = await this.validateQrForShift(userData, job, true, actionType);
            resolve(retryResult);
          },
          () => {
            this.isValidating = false;
            this.pendingValidationData = null;
            this.waitingForPermissionFromSettings = false;
            resolve(false);
          }
        )
        .then((popup) => {
          this.activePopup = popup;
        });
    });
  }

  private async dismissActivePopup(): Promise<void> {
    if (this.activePopup) {
      try {
        await this.activePopup.dismiss();
      } catch (error) {
        // Silent fail
      }
      this.activePopup = null;
    }
  }

  private async hideCurrentLoading(): Promise<void> {
    if (this.currentLoadingRef) {
      try {
        await this.loadingService.hideLoading(this.currentLoadingRef);
      } catch (error) {
        console.error('[QR Validation] Error hiding loading:', error);
      }
      this.currentLoadingRef = null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  ngOnDestroy(): void {
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
      this.permissionSubscription = null;
    }
    
    if (this.permissionStatusSubscription) {
      this.permissionStatusSubscription.unsubscribe();
      this.permissionStatusSubscription = null;
    }
    
    this.cleanup().catch(err => {
      console.error('[QR Validation] Cleanup error:', err);
    });
  }

  async cleanup(): Promise<void> {
    if (this.cleanupInProgress) return;
    this.cleanupInProgress = true;

    try {
      await this.hideCurrentLoading();
      await this.dismissActivePopup();

      const topModal = await this.modalController.getTop();
      if (topModal) {
        try {
          await topModal.dismiss();
        } catch (err) {
          console.error('[QR Validation] Error dismissing modal in cleanup:', err);
        }
      }

      this.isValidating = false;
      this.scannerOpened = false;
      this.validationPromise = null;
      this.permissionRequestInProgress = false;
      this.waitingForPermissionFromSettings = false;
      this.pendingValidationData = null;
    } finally {
      this.cleanupInProgress = false;
    }
  }
}